import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { PendingChip } from "../../components/PendingChip";
import { getCurrentMonthFirstDay } from "../../utils/calendarHelpers";
import { AdvertiserCreditsSection } from "./AdvertiserCreditsSection";
import { fetchMTDSpend } from "../../services/spendService";
import { isAtOrAboveCapThreshold, checkActiveCreative, dispatchActivationJobs, setCampaignStatus } from "../../services/advertiserActivation";

const KNOWN_GEO_TYPES = [
  { value: "zip_code", label: "Zip Code Targeting" },
];

// Diff field definitions for the pending changes section
const PENDING_DIFF_FIELDS = [
  { key: "name",     label: "Company Name" },
  { key: "website",  label: "Website" },
  { key: "category", label: "Category" },
];

const ADVERTISER_SELECT = "enabled_geo_types, status, name, website, category, uuid, budget_limit, target_margin";

// Ensures an attribution_score_threshold row exists for the advertiser, creating one with the
// default threshold if missing. Returns an error message string, or null on success.
async function ensureAttributionThreshold(advertiserId) {
  const { data: threshold, error: lookupError } = await supabase
    .from("attribution_score_threshold")
    .select("id")
    .eq("advertiser_id", advertiserId)
    .maybeSingle();
  if (lookupError) return `failed to check attribution threshold: ${lookupError.message}`;
  if (threshold) return null;

  const { error: insertError } = await supabase
    .from("attribution_score_threshold")
    .insert({ advertiser_id: advertiserId, threshold: 49 });
  if (insertError) return `failed to create attribution threshold: ${insertError.message}`;
  return null;
}

export function AdvertiserSettingsModal({ advertiserId, advertiserName, onClose, onSaved }) {
  const { canManageAdvertiserData, isSupportUser } = useAuth();
  const [enabledGeoTypes, setEnabledGeoTypes] = useState([]);
  const [status, setStatus] = useState("inactive");
  const [budgetLimit, setBudgetLimit] = useState("");
  const [targetMargin, setTargetMargin] = useState("80");
  const [advertiserBaseline, setAdvertiserBaseline] = useState(null);
  const initialStatus = useRef("inactive");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Pending changes state
  const [pendingRow, setPendingRow] = useState(null);
  const [pendingActionError, setPendingActionError] = useState(null);
  const [pendingActioning, setPendingActioning] = useState(false);
  const legacyCampaignSettingsUrl = advertiserBaseline?.uuid
    ? `https://app.audiohook.com/${advertiserBaseline.uuid}/campaign`
    : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setEnabledGeoTypes([]);
    setPendingRow(null);
    setPendingActionError(null);
    if (!supabase || advertiserId == null) { setLoading(false); return; }
    (async () => {
      const [advRes, pendingRes] = await Promise.all([
        supabase
          .from("advertisers")
          .select(ADVERTISER_SELECT)
          .eq("id", advertiserId)
          .maybeSingle(),
        supabase
          .from("advertiser_pending_settings")
          .select("id, name, website, category")
          .eq("advertiser_id", advertiserId)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const { data, error } = advRes;
      if (error || !data) {
        setLoadError(error?.message || "Failed to load advertiser settings.");
        setLoading(false);
        return;
      }
      setEnabledGeoTypes(Array.isArray(data?.enabled_geo_types) ? data.enabled_geo_types : []);
      const st = data?.status ?? "inactive";
      setStatus(st);
      initialStatus.current = st;
      setBudgetLimit(data?.budget_limit != null ? String(data.budget_limit) : "");
      setTargetMargin(data?.target_margin != null ? String(parseFloat((data.target_margin * 100).toFixed(4))) : "80");
      setAdvertiserBaseline(data);

      if (!pendingRes.error && pendingRes.data) {
        setPendingRow(pendingRes.data);
      }

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId]);

  const handleApprove = async () => {
    if (!supabase || !pendingRow) return;
    setPendingActioning(true);
    setPendingActionError(null);
    const { error } = await supabase.rpc("approve_advertiser_pending_settings", { pending_id: pendingRow.id });
    setPendingActioning(false);
    if (error) {
      setPendingActionError(error.message || "Failed to approve changes.");
      return;
    }
    setPendingRow(null);
    // Re-fetch the updated advertiser baseline
    const { data } = await supabase
      .from("advertisers")
      .select(ADVERTISER_SELECT)
      .eq("id", advertiserId)
      .maybeSingle();
    if (data) setAdvertiserBaseline(data);
    onSaved?.();
  };

  const handleReject = async () => {
    if (!supabase || !pendingRow) return;
    setPendingActioning(true);
    setPendingActionError(null);
    const { error } = await supabase
      .from("advertiser_pending_settings")
      .delete()
      .eq("id", pendingRow.id);
    setPendingActioning(false);
    if (error) {
      setPendingActionError(error.message || "Failed to reject changes.");
      return;
    }
    setPendingRow(null);
    onSaved?.();
  };

  const toggleGeoType = (value) => {
    setEnabledGeoTypes(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
  };

  const handleSave = async () => {
    if (!supabase || advertiserId == null || !canManageAdvertiserData) return;
    setSaving(true);
    setSaveError(null);
    const bail = (msg) => { setSaveError(msg); };

    try {
      const parsedBudgetLimit = budgetLimit.trim() ? parseInt(budgetLimit, 10) : null;

      if (isSupportUser) {
        const parsedTargetMargin = parseFloat(targetMargin);
        if (isNaN(parsedTargetMargin) || parsedTargetMargin < 0 || parsedTargetMargin > 100) {
          bail("Target margin must be a number between 0% and 100%.");
          return;
        }
      }

      let warning = null; // set when reactivation is skipped because MTD spend is at/above the cap
      const needsPrerequisiteCheck =
        (initialStatus.current === "inactive" && status !== "inactive") ||
        (initialStatus.current === "paused" && status === "active");

      if (needsPrerequisiteCheck) {
        const monthYear = getCurrentMonthFirstDay();
        const [creativesRes, settingsRes] = await Promise.all([
          checkActiveCreative(advertiserId),
          supabase
            .from("campaign_settings")
            .select("id, spend_cap")
            .eq("advertiser_id", advertiserId)
            .eq("month_year", monthYear)
            .in("status", ["approved", "pending"])
            .order("updated_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);
        if (creativesRes.error || settingsRes.error) {
          bail(`Failed to verify activation prerequisites: ${creativesRes.error?.message || settingsRes.error?.message}`);
          return;
        }
        const errors = [];
        if (!creativesRes.hasActiveCreative) errors.push("no active creatives with an active variation");
        if (!settingsRes.data) errors.push(`no approved/pending campaign settings for ${monthYear}`);
        if (errors.length) { bail(`Cannot change status: ${errors.join(" and ")}.`); return; }

        if (status === "active") {
          const { spendSum, error: spendError } = await fetchMTDSpend(advertiserId);
          if (spendError) { bail(`Failed to verify activation prerequisites: ${spendError.message}`); return; }
          const cap = settingsRes.data?.spend_cap ?? null;
          if (isAtOrAboveCapThreshold(spendSum, cap)) {
            const pct = Math.round((spendSum / cap) * 100);
            warning = `Campaigns were not started for this advertiser: MTD spend ($${spendSum.toLocaleString()}) is already at ${pct}% of the $${cap.toLocaleString()} spend cap.`;
          }
        }
      }

      const updatePayload = {
        enabled_geo_types: enabledGeoTypes,
        status,
        budget_limit: Number.isFinite(parsedBudgetLimit) ? parsedBudgetLimit : null,
        ...(isSupportUser ? { target_margin: parseFloat(targetMargin) / 100 } : {}),
      };

      const { data, error } = await supabase
        .from("advertisers")
        .update(updatePayload)
        .eq("id", advertiserId)
        .select("id")
        .maybeSingle();
      if (error || !data) { bail(error?.message || "Advertiser settings were not saved."); return; }

      const from = initialStatus.current;
      const to = status;
      initialStatus.current = to;

      if (from !== to && to === "active") {
        if (!warning) {
          if (from === "inactive") {
            const thresholdError = await ensureAttributionThreshold(advertiserId);
            if (thresholdError) { bail(`Settings saved, but ${thresholdError}`); return; }
          }

          const { error: jobError } = await dispatchActivationJobs({ advertiserId });
          if (jobError) { bail(`Settings saved, but failed to trigger activation jobs: ${jobError.message}`); return; }
        }

      } else if (from !== to && to === "paused") {
        const { error: pauseError } = await setCampaignStatus({ advertiserId, status: false });
        if (pauseError) { bail(`Settings saved, but failed to trigger pause job: ${pauseError.message}`); return; }
      }

      onSaved?.(warning);
      onClose();
    } catch (err) {
      setSaveError(err?.message || "An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.3)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:200, padding:"20px 0" }} onClick={e => { if(e.target===e.currentTarget) onClose(); }}>
      <div style={{ background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:16, maxWidth:420, width:"90%", maxHeight:"100%", boxShadow:"0 8px 32px rgba(0,0,0,0.15)", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"28px 28px 0", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <h4 style={{ fontSize:16, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Advertiser Settings</h4>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          </div>
          <div style={{ fontSize:12, color:"#7b7b7b", marginBottom:20 }}>
            {advertiserName || `Advertiser #${advertiserId}`}
          </div>
        </div>

        <div style={{ padding:"0 28px 20px", overflowY:"auto", flex:"1 1 auto", minHeight:0 }}>

        {loading && <div style={{ padding:16, textAlign:"center", color:"#7b7b7b", fontSize:13 }}>Loading…</div>}

        {!loading && loadError && (
          <div style={{ padding:"10px 12px", marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>
            Failed to load settings: {loadError}
          </div>
        )}

        {!loading && !loadError && pendingRow && (
          <div style={{ marginBottom:20, background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:12, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontWeight:600, color:"#151515", fontSize:13 }}>Pending Changes</span>
                <PendingChip />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button
                  onClick={handleReject}
                  disabled={pendingActioning}
                  style={{ padding:"5px 14px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, fontWeight:500, cursor: pendingActioning ? "not-allowed" : "pointer" }}
                >Reject</button>
                <button
                  onClick={handleApprove}
                  disabled={pendingActioning}
                  style={{ padding:"5px 14px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor: pendingActioning ? "not-allowed" : "pointer", opacity: pendingActioning ? 0.7 : 1 }}
                >Approve</button>
              </div>
            </div>
            {pendingActionError && (
              <div style={{ padding:"8px 10px", marginBottom:10, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:12 }}>{pendingActionError}</div>
            )}
            {PENDING_DIFF_FIELDS.filter(f => pendingRow[f.key] != null).map(f => (
              <div key={f.key} style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:2 }}>{f.label}</div>
                <div style={{ display:"flex", gap:8, alignItems:"center", fontSize:12 }}>
                  <span style={{ color:"#B8B8B8", textDecoration:"line-through" }}>{advertiserBaseline?.[f.key] ?? "—"}</span>
                  <span style={{ color:"#151515" }}>→</span>
                  <span style={{ color:"#F90091", fontWeight:600 }}>{pendingRow[f.key]}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && !loadError && (
          <>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Credit Limit</div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"8px 12px", width:"100%" }}>
                  <span style={{ color:"#7b7b7b", fontSize:13 }}>$</span>
                  <input
                    value={budgetLimit}
                    onChange={(e) => setBudgetLimit(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="No limit"
                    style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:14, width:"100%", marginLeft:4 }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Internal UI</div>
              <button
                type="button"
                onClick={() => {
                  if (!legacyCampaignSettingsUrl) return;
                  window.open(legacyCampaignSettingsUrl, "_blank", "noopener,noreferrer");
                }}
                disabled={!legacyCampaignSettingsUrl}
                style={{ padding:"9px 12px", background:"#FFF5FB", color:"#F90091", border:"1px solid #F9009133", borderRadius:8, fontSize:13, fontWeight:600, cursor: legacyCampaignSettingsUrl ? "pointer" : "not-allowed", opacity: legacyCampaignSettingsUrl ? 1 : 0.5 }}
              >
                Internal Campaigns Settings
              </button>
            </div>

            <div style={{ marginBottom:20, display:"flex", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Advertiser Status</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!canManageAdvertiserData}
                  style={{ width:"100%", padding:"9px 32px 9px 12px", background:`#FAFAFA url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 12px center / 10px 6px`, border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, fontFamily:"inherit", outline:"none", appearance:"none", WebkitAppearance:"none", MozAppearance:"none", cursor: canManageAdvertiserData ? "pointer" : "not-allowed", opacity: canManageAdvertiserData ? 1 : 0.65 }}
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {isSupportUser && (
                <div style={{ flex:"0 0 110px" }}>
                  <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Target Margin</div>
                  <div style={{ display:"flex", alignItems:"center", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"8px 12px", height:"100%", boxSizing:"border-box", maxHeight:38 }}>
                    <input
                      value={targetMargin}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^(\d*\.?\d*)$/.test(v)) setTargetMargin(v);
                      }}
                      placeholder="80"
                      aria-label="Target margin percentage"
                      style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:14, width:"100%", minWidth:0 }}
                    />
                    <span style={{ color:"#7b7b7b", fontSize:13, flexShrink:0 }}>%</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:12 }}>Geo Targeting Features</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {KNOWN_GEO_TYPES.map(type => {
                  const selected = enabledGeoTypes.includes(type.value);
                  return (
                    <button
                      type="button"
                      key={type.value}
                      onClick={() => canManageAdvertiserData && toggleGeoType(type.value)}
                      disabled={!canManageAdvertiserData}
                      aria-pressed={selected}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background: selected ? "#FFF5FB" : "#FAFAFA", borderRadius:8, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", border:`1px solid ${selected ? "#F90091" : "#E2E2E2"}`, opacity: canManageAdvertiserData ? 1 : 0.65 }}>
                      <div style={{ width:16, height:16, borderRadius:4, background: selected ? "#F90091" : "#E2E2E2", border:`1px solid ${selected ? "#F90091" : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {selected && <span style={{ color:"#fff", fontSize:10, lineHeight:1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize:13, color: selected ? "#151515" : "#626262" }}>{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {isSupportUser && <AdvertiserCreditsSection advertiserId={advertiserId} />}

            {saveError && (
              <div style={{ padding:"10px 12px", background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>{saveError}</div>
            )}
          </>
        )}
        </div>

        {!loading && (
          <div style={{ padding:"16px 28px", borderTop:"1px solid #E2E2E2", display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0 }}>
            {loadError ? (
              <button onClick={onClose} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Close</button>
            ) : (
              <>
                <button onClick={onClose} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving || !canManageAdvertiserData} style={{ padding:"9px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: saving || !canManageAdvertiserData ? "not-allowed" : "pointer", opacity: saving || !canManageAdvertiserData ? 0.7 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
